import React from 'react';
import config from '../config';

// =============================================================================
// MemoryModal - kelola Memori (memori jangka panjang yang ditulis AI lewat
// tool save_memory). Prinsip kepercayaan dari MEMORY_FEATURE_PLAN.md: semua
// yang diingat terlihat, bisa dihapus per baris, dan seluruh fiturnya bisa
// dimatikan. Dibuka dari menu akun di sidebar.
// =============================================================================

const BASE_URL = config.apiUrl;

interface MemoryRow {
  id: number;
  kind: string;
  content: string;
  created_at: string | null;
}

interface MemoryModalProps {
  open: boolean;
  onClose: () => void;
  theme: 'light' | 'dark';
  c: any; // theme color tokens (same object Sidebar uses)
}

const KIND_LABEL: Record<string, string> = {
  identity: 'Identitas',
  preference: 'Preferensi',
  project: 'Project',
  fact: 'Fakta',
};

const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('accessToken');
  const tokenType = localStorage.getItem('tokenType') || 'Bearer';
  return token ? { Authorization: `${tokenType} ${token}` } : {};
};

const MemoryModal: React.FC<MemoryModalProps> = ({ open, onClose, theme, c }) => {
  const [rows, setRows] = React.useState<MemoryRow[]>([]);
  const [enabled, setEnabled] = React.useState(true);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${BASE_URL}/memories`, { headers: authHeaders() });
        if (!res.ok) throw new Error(String(res.status));
        const data = await res.json();
        if (cancelled) return;
        setEnabled(data.enabled !== false);
        setRows(Array.isArray(data.memories) ? data.memories : []);
      } catch {
        if (!cancelled) setError('Gagal memuat memori. Coba lagi.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (confirmDeleteId != null) {
        e.stopPropagation();
        setConfirmDeleteId(null);
        return;
      }
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, confirmDeleteId]);

  const remove = async (id: number) => {
    const prev = rows;
    setRows(rows.filter((r) => r.id !== id));
    try {
      const res = await fetch(`${BASE_URL}/memories/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok && res.status !== 404) throw new Error(String(res.status));
    } catch {
      setRows(prev); // kegagalan jaringan: kembalikan baris
    }
  };

  const toggle = async () => {
    const next = !enabled;
    setEnabled(next);
    try {
      const res = await fetch(`${BASE_URL}/memories/settings`, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: next }),
      });
      if (!res.ok) throw new Error(String(res.status));
    } catch {
      setEnabled(!next);
    }
  };

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1200,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Memori"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: '480px', maxHeight: '80vh',
          display: 'flex', flexDirection: 'column',
          background: theme === 'dark' ? '#141619' : '#FFFFFF',
          border: `1px solid ${c.line2}`,
          borderRadius: '14px',
          boxShadow: c.shadow,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 16px 12px', borderBottom: `1px solid ${c.line}`,
        }}>
          <div>
            <div style={{ fontSize: '14px', fontWeight: 600, color: c.ink }}>Memori</div>
            <div style={{ fontSize: '11.5px', color: c.ink3, marginTop: '2px' }}>
              Hal tentang kamu yang diingat Power AI antar percakapan.
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Tutup"
            style={{
              border: 'none', background: 'none', cursor: 'pointer',
              color: c.ink3, fontSize: '18px', lineHeight: 1, padding: '4px 6px',
            }}
          >×</button>
        </div>

        {/* Toggle */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '11px 16px', borderBottom: `1px solid ${c.line}`,
        }}>
          <div style={{ fontSize: '12.5px', color: c.ink2 }}>
            Aktifkan memori
            <div style={{ fontSize: '11px', color: c.ink3, marginTop: '1px' }}>
              Nonaktif = AI tidak mengingat dan tidak memakai memori lama.
            </div>
          </div>
          <button
            role="switch"
            aria-checked={enabled}
            onClick={toggle}
            style={{
              width: '36px', height: '20px', borderRadius: '999px', border: 'none',
              cursor: 'pointer', position: 'relative', flexShrink: 0,
              background: enabled ? c.jade : (theme === 'dark' ? '#2A2D31' : '#D1D5DB'),
              transition: 'background 0.15s ease',
            }}
          >
            <span style={{
              position: 'absolute', top: '2px',
              left: enabled ? '18px' : '2px',
              width: '16px', height: '16px', borderRadius: '50%',
              background: '#FFFFFF', transition: 'left 0.15s ease',
            }} />
          </button>
        </div>

        {/* List */}
        <div style={{ overflowY: 'auto', padding: '6px 8px 10px', flex: 1 }}>
          {loading && (
            <div style={{ padding: '18px 8px', fontSize: '12px', color: c.ink3 }}>Memuat…</div>
          )}
          {error && (
            <div style={{ padding: '18px 8px', fontSize: '12px', color: theme === 'light' ? '#DC2626' : '#F87171' }}>{error}</div>
          )}
          {!loading && !error && rows.length === 0 && (
            <div style={{ padding: '18px 8px', fontSize: '12px', color: c.ink3, lineHeight: 1.6 }}>
              Belum ada memori. Saat kamu menyebut preferensi atau hal penting
              tentang dirimu di chat, Power AI akan menyimpannya di sini
              (ditandai chip "Disimpan ke memori"). Kamu juga bisa langsung
              minta: "ingat ya, aku suka jawaban singkat".
            </div>
          )}
          {rows.map((r) => (
            <div
              key={r.id}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: '8px',
                padding: '8px 8px', borderRadius: '9px',
              }}
            >
              <span style={{
                fontSize: '10px', color: c.ink3, fontFamily: c.mono,
                border: `1px solid ${c.line}`, borderRadius: '999px',
                padding: '1px 7px', flexShrink: 0, marginTop: '1px',
                letterSpacing: '0.02em',
              }}>
                {KIND_LABEL[r.kind] || r.kind}
              </span>
              <span style={{ flex: 1, minWidth: 0, fontSize: '12.5px', color: c.ink2, lineHeight: 1.5 }}>
                {r.content}
              </span>
              {confirmDeleteId === r.id ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                  <button
                    type="button"
                    autoFocus
                    onClick={() => {
                      setConfirmDeleteId(null);
                      void remove(r.id);
                    }}
                    aria-label="Yakin hapus memori ini"
                    style={{
                      border: 'none', background: 'none', cursor: 'pointer',
                      color: theme === 'light' ? '#DC2626' : '#F87171',
                      fontSize: '12px', fontWeight: 600, fontFamily: 'inherit',
                      padding: '2px 0',
                    }}
                  >
                    Yakin hapus?
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDeleteId(null)}
                    style={{
                      border: 'none', background: 'none', cursor: 'pointer',
                      color: c.ink3, fontSize: '12px', fontFamily: 'inherit',
                      padding: '2px 0',
                    }}
                  >
                    Batal
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmDeleteId(r.id)}
                  aria-label="Hapus memori ini"
                  title="Hapus"
                  style={{
                    border: 'none', background: 'none', cursor: 'pointer',
                    color: c.ink3, fontSize: '14px', lineHeight: 1, padding: '2px 4px', flexShrink: 0,
                  }}
                >×</button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MemoryModal;
